import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { resolveApiErrorMessage } from '@/api/resolveApiErrorMessage';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ModalOverlay } from '@/components/ui/modalOverlay';
import { Icon } from '@/components/common/Icon';
import { showSuccessNotification } from '@/utils/notifications';
import { useCreateWorkspaceInvitationMutation } from '@/hooks/useWorkspaceTeam';

interface InviteMemberModalProps {
  onClose: () => void;
}

export function InviteMemberModal({ onClose }: InviteMemberModalProps) {
  const { t } = useTranslation('team');
  const createInvitationMutation = useCreateWorkspaceInvitationMutation();
  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [inviteLink, setInviteLink] = useState('');

  async function submitInvitation(formEvent: FormEvent<HTMLFormElement>) {
    formEvent.preventDefault();
    setErrorMessage('');
    if (email.trim().length === 0) {
      setErrorMessage(t('invite.emailRequired'));
      return;
    }
    try {
      const created = await createInvitationMutation.mutateAsync({
        email: email.trim(),
        fullName: fullName.trim().length === 0 ? null : fullName.trim(),
      });
      setInviteLink(created.inviteLink);
      showSuccessNotification(t('invite.sent'));
    } catch (inviteError) {
      const backendMessage = resolveApiErrorMessage(inviteError);
      setErrorMessage(backendMessage.length > 0 ? backendMessage : t('invite.error'));
    }
  }

  function copyInviteLink() {
    void navigator.clipboard.writeText(inviteLink);
    showSuccessNotification(t('invite.linkCopied'));
  }

  return (
    <ModalOverlay onRequestClose={onClose} ariaLabel={t('invite.title')}>
      <div className="w-full max-w-md bg-zoowhite rounded-2xl p-6 shadow-xl">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-zooindigo">{t('invite.title')}</h2>
          <Button variant="ghost" size="icon" aria-label={t('invite.close')} onClick={onClose}>
            <Icon name="X" />
          </Button>
        </div>

        {inviteLink.length > 0 ? (
          <div className="space-y-3">
            <p className="text-sm text-zoomuted">{t('invite.linkTitle')}</p>
            <div className="flex items-center gap-2 rounded-xl border border-zooborder bg-zoooffwhite px-3 py-2">
              <span className="text-xs text-zooindigo truncate">{inviteLink}</span>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={copyInviteLink}>
                <Icon name="Copy" />
                {t('invite.copyLink')}
              </Button>
              <Button onClick={onClose}>{t('invite.done')}</Button>
            </div>
          </div>
        ) : (
          <form onSubmit={submitInvitation} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="invite-email">{t('invite.email')}</Label>
              <Input
                id="invite-email"
                type="email"
                inputMode="email"
                value={email}
                onChange={function updateEmail(changeEvent) {
                  setEmail(changeEvent.target.value);
                }}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="invite-full-name">{t('invite.fullName')}</Label>
              <Input
                id="invite-full-name"
                value={fullName}
                placeholder={t('invite.fullNamePlaceholder')}
                onChange={function updateFullName(changeEvent) {
                  setFullName(changeEvent.target.value);
                }}
              />
            </div>

            {errorMessage.length > 0 ? (
              <div
                role="alert"
                className="flex items-center gap-2 px-3.5 py-2.5 bg-zooerrorbg border border-zooerrorborder rounded-xl text-xs font-medium text-zooerrortext"
              >
                <Icon name="AlertCircle" />
                {errorMessage}
              </div>
            ) : null}

            <div className="flex justify-end gap-2">
              <Button variant="outline" type="button" onClick={onClose}>
                {t('invite.cancel')}
              </Button>
              <Button type="submit" isLoading={createInvitationMutation.isPending}>
                {t('invite.submit')}
              </Button>
            </div>
          </form>
        )}
      </div>
    </ModalOverlay>
  );
}
